import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export type LatestReleaseInfo = {
  version: string
  published_at: string | null
  updated_by: string
}

const CACHE_KEY = 'suite:last-release-info'

function readCache(): LatestReleaseInfo | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? JSON.parse(raw) as LatestReleaseInfo : null
  } catch {
    return null
  }
}

function writeCache(value: LatestReleaseInfo) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(value))
  } catch {
    // Cache é contingência; falha de armazenamento não bloqueia a aplicação.
  }
}

function formatPublishedAt(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  const day = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date)

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)

  return `${day} às ${time}`
}

export function useLatestReleaseInfo(enabled: boolean) {
  const [info, setInfo] = useState<LatestReleaseInfo | null>(() => readCache())
  const [offline, setOffline] = useState(false)

  useEffect(() => {
    // A função SQL é restrita a authenticated: nunca consultar durante o login.
    if (!enabled) return
    let active = true
    let inFlight = false

    async function refresh() {
      if (inFlight || !active) return
      if (!navigator.onLine) {
        setOffline(Boolean(readCache()))
        return
      }
      inFlight = true
      try {
        const result = await supabase.rpc('get_latest_release_info')
        if (!active) return

        if (result.error) {
          setOffline(Boolean(readCache()))
          return
        }

        const row = Array.isArray(result.data) ? result.data[0] : result.data
        if (row) {
          const fresh = row as LatestReleaseInfo
          writeCache(fresh)
          setInfo(fresh)
          setOffline(false)
        }
      } catch {
        if (active) setOffline(Boolean(readCache()))
      } finally {
        inFlight = false
      }
    }

    void refresh()
    const interval = window.setInterval(() => void refresh(), 60_000)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      active = false
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled])

  return { info, offline }
}

export function LastUpdateCard({ info, offline = false }: { info: LatestReleaseInfo | null; offline?: boolean }) {
  if (!info) return null

  return (
    <section className="last-update-card" aria-label="Última atualização publicada">
      <div className="last-update-card__head">
        <span className="last-update-dot" aria-hidden="true" />
        <strong>Última Atualização</strong>
        {offline && <span className="last-update-cache">cache</span>}
      </div>
      <div className="last-update-card__body">
        <span><b>Data:</b> {formatPublishedAt(info.published_at)}</span>
        <span><b>Atualizado por:</b> {info.updated_by}</span>
      </div>
    </section>
  )
}
